import { createFocusTrap, FocusTrap } from 'focus-trap';
import { html, nothing, PropertyValues, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCancelEvent } from '../../events/blocks-cancel';
import { BlocksModalCloseEvent } from '../../events/blocks-modal-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import { customConverter } from '../../util/custom-converter';
import { KEYS } from '../../util/event-keys';
import { lockBodyScrolling, unlockBodyScrolling } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../heading/heading';
import '../icon/icons/arrow-back';
import '../icon/icons/close';
import type { HeadingTagName, ModalHeadingVariant, ModalOrientation, ModalSize, ModalVerticalMaxHeight } from './modal.model';
import styles from './modal.styles';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';

/**
 * @slot default - The modal content
 * @slot header - Content appearing above the default slot
 * @slot footer - Content appearing below the default slot
 * @slot actions - For action content in header area
 * @fires bl-modal-open - emitted when component is opened
 * @fires bl-modal-close - emitted when component is closed
 * @fires bl-modal-cancel - emitted when modal is cancelled
 * @csspart base - The component's base wrapper.
 * @csspart window - The component's window wrapper.
 * @csspart back-button - The component's back-button wrapper.
 * @csspart back-before - The component's back-before before slot.
 * @csspart content-wrapper - The component's content wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-heading - The component's `<cat-heading>`.
 * @csspart part-header - The component's header slot.
 * @csspart part-actions - The component's actions slot.
 * @csspart close-button - The component's close-button.
 * @csspart close-after - The component's close button after slot.
 * @csspart bottom-wrapper - The component's wrapper for body and footer.
 * @csspart body - The component's body wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksModal extends BlocksElement {static styles: CSSResultGroup = styles;

  protected slots = new SlotController(this, 'header', null, 'footer', 'actions');
  /**
   * Close button text
   */
  @property({ type: String })
  closeButtonText = 'Close';

  /**
   * Back button text
   */
  @property({ type: String })
  backButtonText = 'Back';

  /**
   * Toggle the back button
   */
  @property({ type: Boolean })
  backButton: boolean | undefined;

  /**
   * Size variant
   * <cat-text-passage size="lg">
   * <ul>
   * <li>**lg** increases the modal size</li>
   * <li>**sm** decreases the modal size</li>
   * <li>**md** sets the modal size accordingly b/w lg and sm</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: ModalSize = 'sm';

  /**
   * show/hide close icon in modal header
   */
  @property({
    // Allow "true|false" attribute values but keep the property boolean
    type: Boolean,
    converter: customConverter()
  })
  dismissible = true;

  /**
   * Toggle modal window to full width of screen
   */
  @property({ type: Boolean })
  fullWidth: boolean | undefined;

  /**
   * Disabling modal close on backdrop click
   */
  @property({ type: Boolean })
  disableBackdropClick = false;

  /**
   * Disabling modal close on backdrop click
   */
  @property({ type: Boolean })
  disableEscClick = false;

  /**
   * The text that displays in the modal header.
   * 1) Will only display a max of two lines.
   */
  @property({ type: String })
  heading: string | undefined;

  /**
   * width of modal window
   *
   */
  @property({ type: String })
  width: string | undefined;

  /**
   * Modal heading variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**display-xl** renders a heading with the cat-theme-typography-preset-display-xl preset</li>
   * <li>**display-lg** renders a heading with the cat-theme-typography-preset-display-lg preset</li>
   * <li>**display** renders a heading with the cat-theme-typography-preset-display preset</li>
   * <li>**display-sm** renders a heading with the cat-theme-typography-preset-display-sm preset</li>
   * <li>**headline-lg** renders a heading with the cat-theme-typography-preset-headline-lg preset</li>
   * <li>**headline** renders a heading with the cat-theme-typography-preset-headline preset</li>
   * <li>**title** renders a heading with the cat-theme-typography-preset-title preset</li>
   * <li>**title-sm** renders a heading with the cat-theme-typography-preset-title-sm</li>
   * <li>**label-lg** renders a heading with the cat-theme-typography-preset-label-lg</li>
   * <li>**label** renders a heading with the cat-theme-typography-preset-label</li>
   * <li>**label-sm** renders a heading with the cat-theme-typography-preset-label-sm</li>
   * <li>**body** renders a heading with the cat-theme-typography-preset-body preset</li>
   * <li>**body-sm** renders a heading with the cat-theme-typography-preset-body-sm preset</li>
   * <li>**footnote** renders a heading with the cat-theme-typography-preset-footnote preset</li>
   * <li>**footnote-bold** renders a heading with the cat-theme-typography-preset-footnote-bold preset</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  headingVariant: ModalHeadingVariant = 'title';

  /**
   * Modal heading tagname
   * 1) This is needed to use proper semantic heading treatments depending on where it lives on the page
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**h1** renders an `h1` tag</li>
   * <li>**h2** renders an `h2` tag. This is the default</li>
   * <li>**h3** renders an `h3` tag</li>
   * <li>**h4** renders an `h4` tag</li>
   * <li>**h5** renders an `h5` tag</li>
   * <li>**h6** renders an `h6` tag</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  headingTagName: HeadingTagName = 'h2';

  /**
   * Active property
   * 1) If true, modal appears. If false, modal is hidden.
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * @default true , controls whether focus should return to the previously focused element before the modal opens
   */
  @property({ type: Boolean }) returnFocusOnDeactivate = true;

  /**
   * Aria labelled by attribute
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * Enable an option to have Modal open from bottom in mobile
   */
  @property({ type: String })
  orientation: ModalOrientation = 'side';

  /**
   * Set max Height for orientation bottom Modal
   */
  @property({ type: String })
  verticalMaxHeight: ModalVerticalMaxHeight = 'default';

  /**
   * **Mobile Break Point, Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Provides a unique identifier to group the dismissible button with its
   * aria-describedby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) dismissibleButtonId: string | undefined;

  /**
   * Provides a unique identifier to group the actions button with its
   * aria-describedby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) actionsId: string | undefined;

  /**
   * Provides a unique identifier to group the  back button with its
   * aria-describedby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) backButtonId: string | undefined;

  /**
   * Provides a unique identifier to group the footer wrapper container with its
   * aria-describedby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) footerWrapperId: string | undefined;

  /**
   * Provides a unique identifier to group the body wrapper container with its
   * aria-describedby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) bodyWrapperId: string | undefined;

  @query('dialog', true) private dialogElement!: HTMLDialogElement;

  /**
   * Mobile Variant check for orientation property.
   */
  @state() private isMobile = false;

  private _focusTrap?: FocusTrap;

  private previouslyActiveElement: Element | null | undefined;

  private wasActive = false;

  /**
   * Returns ids of associated element
   */
  private get ariaDescribedbyIds(): string {
    return [this.backButtonId, this.actionsId, this.dismissibleButtonId, this.footerWrapperId, this.bodyWrapperId].filter(Boolean).join(' ');
  }

  /**
   * Initialize/Update
   * 1) Set the id and ariaLabelledBy
   * 2) Pause/Unpause focustrap on contextual menu open and close
   * 3) Pause/Unpause focustrap on dropdown panel open and close
   */
  connectedCallback() {
    super.connectedCallback();

    this.ariaLabelledBy = this.ariaLabelledBy || nanoid();
    this.addEventListener('bl-contextual-menu-open', () => {
      this._focusTrap?.pause(); /* 2 */
    });

    this.addEventListener('bl-contextual-menu-close', () => {
      this._focusTrap?.unpause(); /* 2 */
    });
    // Without this event listener keyboard functionality of dropdown will be blocked by focustrap
    this.addEventListener('bl-dropdown-open', this.pauseFocusTrap, true); /* 3 */
    this.addEventListener('bl-dropdown-close', this.unPauseFocusTrap, true); /* 3 */
    window.addEventListener('resize', this.setMobile);
  }

  disconnectedCallback(): void {
    /**To deactivate focus trap */
    this._focusTrap?.deactivate();
    this.removeEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
    this.removeEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
    window.removeEventListener('resize', this.setMobile);
    unlockBodyScrolling(this); /** In case of conditional rendering used to unlock body scroll */
  }

  /**
   *  Pause the focustrap
   */
  private pauseFocusTrap() {
    this._focusTrap?.pause();
  }

  /**
   *  Query the Body
   */
  private get queryBody() {
    return document.querySelector('body')!;
  }

  /**
   *  Unpause the focustrap
   */
  private unPauseFocusTrap() {
    this._focusTrap?.unpause();
  }

  private setAutoFocus(modalWindow: HTMLElement) {
    const autoFocusTarget = this.querySelector('[autoFocus]') || this.shadowRoot?.querySelector('[autoFocus]');
    if (autoFocusTarget) {
      (autoFocusTarget as HTMLElement).focus();
    } else {
      modalWindow.focus();
    }
  }

  /**
   *  When the trigger element is clicked and the modal is about to open, blur the trigger element to remove focus from it
   * provided there was an already activeElement and returnFocusOnDeactivate is false
   */
  private blurTriggerElement() {
    if (this.previouslyActiveElement && !this.returnFocusOnDeactivate) {
      (this.previouslyActiveElement as HTMLElement).blur();
    }
  }

  /**
   *  Update the modal state
   * 1) If the modal is active, call the function to lock body scroll.
   * 2) If the modal is not active, call the function to unlock body scroll.
   */
  protected updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('isActive')) {
      this.wasActive = changedProperties.get('isActive') === undefined ? false : true;
      if (this.isActive) {
        if (!this.previouslyActiveElement) {
          this.previouslyActiveElement = document.activeElement;
        }
        this.blurTriggerElement();
        this.activateFocusTrap();
        if (this.width && !this.fullWidth) {
          const modalWindow = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-modal__window')!;
          modalWindow.style.width = this.width;
        }

        const body = this.queryBody;
        /* 1 */
        if (body) {
          lockBodyScrolling(this, 'stable');
        }
        if (!this.isInline) {
          this.dialogElement?.showModal();
        } else {
          this.dialogElement?.show();
        }
        this.dispatch({ eventName: 'bl-modal-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
      } else {
        this._focusTrap?.deactivate(); /**To deactivate focus trap */
        /* 2 */
        const body = this.queryBody;
        if (body && this.wasActive) {
          unlockBodyScrolling(this);
        }

        if (this.returnFocusOnDeactivate) {
          (this.previouslyActiveElement as HTMLElement)?.focus();
          this.previouslyActiveElement = null;
        }
        this.dialogElement?.close();
      }
    }
    if (changedProperties.has('mobileBreakPoint')) {
      this.setMobile();
    }
  }

  private activateFocusTrap(): void {
    setTimeout(() => {
      const modalWindow = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-modal__window')!;
      this.setAutoFocus(modalWindow);

      /** calling function to create focus trap */
      this._focusTrap = createFocusTrap(modalWindow, {
        onActivate: () => modalWindow.classList.add('is-active'),
        onDeactivate: () => modalWindow.classList.remove('is-active'),
        initialFocus: false,
        tabbableOptions: { getShadowRoot: true },
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        allowOutsideClick: true,
        escapeDeactivates: !this.disableEscClick
      });
      /**To activate focus trap */
      this._focusTrap.activate();
    }, 200);
  }

  /**
   * Close modal function
   * 1) Closes the modal
   * 2) Emit close event after closing Modal
   */
  private closeModal(closeType: string) {
    this._focusTrap?.deactivate();
    if (this.emitCancelEvent()) {
      return;
    }

    this.close(); /* 1 */
    this.emitCloseEvent(closeType); /**2 */
  }

  /**
   * Handle "click outside"
   * 1) onClick of the area around the modal window, close the modal.
   * @fire close event when we click close modal
   */
  private handleOnClickOutside(e: Event) {
    if (!this.disableBackdropClick) {
      const modalWindow = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-modal__window');
      const eventTarget = e.target as HTMLElement;
      if (this.isActive && modalWindow && eventTarget instanceof HTMLDialogElement) {
        this.closeModal('backdropclick'); /* 1 */
      }
    }
  }

  /**
   * Handle keydown
   * 1) Close the modal when escape is hit when the user is focused within the modal
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === KEYS.ESCAPE && !this.disableEscClick) {
      this.closeModal('esc'); /* 2 */
    }
    if (e.key === KEYS.ESCAPE && this.disableEscClick) {
      e.preventDefault();
    }
  }

  /**
   * @fire close event
   */
  private emitCloseEvent(closeType: string) {
    this.dispatch({ eventName: 'bl-modal-close', detailObj: { type: closeType, isExpand: this.isActive } }) as BlocksModalCloseEvent;
  }

  /**
   * @fires cancel If we call preventDefault on cancel event object to not close modal.
   * @returns boolean
   */
  private emitCancelEvent(): boolean {
    const cancelEvent = this.dispatch({
      eventName: 'bl-modal-cancel',
      detailObj: {},
      optionsObj: { cancelable: true }
    }) as BlocksCancelEvent;

    return cancelEvent.defaultPrevented;
  }

  /**
   * opens a modal
   */
  public open() {
    this.isActive = true;
  }

  /**close the current modal */
  public close() {
    this.isActive = false;
  }

  /**
   * Checks if screen size is below mobileBreakPoint property.
   */
  private setMobile = () => {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (width < this.mobileBreakPoint) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  };

  /**
   * By default, a dialog invoked by the showModal() method can be dismissed by pressing the Esc key.
   * When we use disableEscClick we should prevent this behavior
   */
  private preventModalClose(event: Event): void {
    if (this.disableEscClick) {
      event.preventDefault();
    } else {
      this.closeModal('cancel');
    }
  }

  render(): TemplateResult<1> {
    const isOrientationBottom = this.orientation == 'bottom' && this.isMobile;

    const componentClassName = this.componentClassNames('cat-c-modal', {
      'cat-c-modal--full-width': this.fullWidth === true,
      'cat-is-active': this.isActive === true,
      'cat-c-modal--lg': this.size === 'lg' && !this.fullWidth,
      'cat-c-modal--md': this.size === 'md' && !this.fullWidth,
      'cat-c-modal--orientation-bottom': isOrientationBottom,
      'cat-c-modal--vertical-height-default': isOrientationBottom && this.verticalMaxHeight == 'default',
      'cat-c-modal--vertical-height-large': isOrientationBottom && this.verticalMaxHeight == 'large',
      'cat-c-modal--has-header': !this.heading && this.slots.hasSlotted('header'),
      'cat-c-modal--has-actions': this.slots.hasSlotted('actions'),
      'cat-c-modal--has-footer': this.slots.hasSlotted('footer')
    });

    return html`
      <div part="base" class="${componentClassName}" @keydown=${this.handleKeyDown} @click=${this.handleOnClickOutside}>
        <dialog
          part="window"
          class="cat-c-modal__window"
          aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
          aria-describedby=${this.ariaDescribedbyIds}
          aria-modal="true"
          @cancel=${this.preventModalClose}
          tabindex="-1"
        >
          <div part="content-wrapper" class="cat-c-modal__wrapper">
            <header part="part-header" class="cat-c-modal__header">
              ${this.backButton
                ? html`
                    <cat-button
                      part="back-button"
                      exportparts=${this.getExportParts('button-back', 'button')}
                      class="cat-c-modal__back-button"
                      variant="ghost"
                      ?hideText=${true}
                      @click=${() => this.closeModal('close')}
                      id=${ifDefined(this.backButtonId)}
                      ?autoFocus=${true}
                    >
                      ${this.backButtonText}
                      <cat-icon-arrow-back
                        part="back-before"
                        exportparts=${this.getExportParts('arrow-back', 'icon')}
                        slot="before"
                      ></cat-icon-arrow-back>
                    </cat-button>
                  `
                : html`${nothing}`}
              <div part="header-wrapper" class="cat-c-modal__header-content" id=${ifDefined(this.ariaLabelledBy)}>
                ${this.heading
                  ? html`
                      <cat-heading
                        part="part-heading"
                        exportparts=${this.getExportParts('heading')}
                        class="cat-c-modal__heading"
                        level=${ifDefined(this.headingTagName)}
                        variant=${ifDefined(this.headingVariant)}
                      >
                        ${this.heading}
                      </cat-heading>
                    `
                  : html`${nothing}`}
                <slot part="part-header" name="header"></slot>
              </div>
              <slot part="part-actions" id=${ifDefined(this.actionsId)} name="actions"></slot>
              ${this.dismissible
                ? html`<cat-button
                    part="close-button"
                    exportparts=${this.getExportParts('button-close', 'button')}
                    class="cat-c-modal__close-button"
                    variant="ghost"
                    ?hideText=${true}
                    @click=${() => this.closeModal('close')}
                    id=${ifDefined(this.dismissibleButtonId)}
                    ?autoFocus=${true}
                  >
                    ${this.closeButtonText}
                    <cat-icon-close part="close-after" exportparts=${this.getExportParts('close', 'icon')} slot="after"></cat-icon-close>
                  </cat-button>`
                : html`${nothing}`}
            </header>
            <div part="bottom-wrapper" class="cat-c-modal--bottom-wrapper">
              <div part="body" class="cat-c-modal__body" id=${ifDefined(this.bodyWrapperId)}><slot part="part-default"></slot></div>
              <div id=${ifDefined(this.footerWrapperId)} part="footer-wrapper" class="cat-c-modal__footer">
                <slot part="part-footer" name="footer"></slot>
              </div>
            </div>
          </div>
        </dialog>
      </div>
    `;
  }
}

if (customElements.get('cat-modal') === undefined) {
  customElements.define('cat-modal', BlocksModal);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-modal': BlocksModal;
  }
}
